import { motion } from 'framer-motion';
import { formatCurrency } from '../../utils';

interface PriceDisplayProps {
  amount: number;
  isCredit?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCents?: boolean;
  label?: string;
  className?: string;
}

export function PriceDisplay({
  amount,
  isCredit = false,
  size = 'md',
  showCents = true,
  label,
  className = '',
}: PriceDisplayProps) {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-2xl',
    xl: 'text-4xl',
  };

  const displayAmount = Math.abs(amount);
  const formatted = formatCurrency(displayAmount, showCents);

  return (
    <div className={`inline-flex flex-col ${className}`}>
      {label && (
        <span className="text-xs uppercase tracking-wider text-gray-500 mb-1">
          {label}
        </span>
      )}
      <span
        className={`font-mono font-bold ${sizeClasses[size]} ${
          isCredit ? 'text-accent-success' : 'text-hul-navy'
        }`}
      >
        {isCredit && '-'}
        {formatted}
      </span>
    </div>
  );
}

interface PriceTagProps {
  amount: number;
  isCredit?: boolean;
  showCents?: boolean;
}

export function PriceTag({ amount, isCredit = false, showCents = true }: PriceTagProps) {
  const displayAmount = Math.abs(amount);
  const formatted = formatCurrency(displayAmount, showCents);

  return (
    <motion.span
      className={`price-tag ${isCredit ? 'price-tag-credit' : ''}`}
      whileHover={{ scale: 1.02 }}
    >
      {isCredit && '-'}
      {formatted}
    </motion.span>
  );
}

interface PriceSummaryRowProps {
  label: string;
  amount: number;
  isCredit?: boolean;
  isTotal?: boolean;
  showCents?: boolean;
}

export function PriceSummaryRow({
  label,
  amount,
  isCredit = false,
  isTotal = false,
  showCents = true,
}: PriceSummaryRowProps) {
  const displayAmount = Math.abs(amount);
  const formatted = formatCurrency(displayAmount, showCents);

  if (isTotal) {
    return (
      <div className="flex justify-between items-center py-4 border-t-2 border-hul-navy">
        <span className="font-heading text-xl uppercase tracking-wider text-hul-navy">
          {label}
        </span>
        <span className="font-mono font-bold text-3xl text-hul-red">
          {formatted}
        </span>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center py-2">
      <span className={`${isCredit ? 'text-accent-success' : 'text-gray-600'}`}>
        {label}
      </span>
      <span
        className={`font-mono font-medium ${
          isCredit ? 'text-accent-success' : 'text-gray-900'
        }`}
      >
        {isCredit && '('}
        {isCredit && '-'}
        {formatted}
        {isCredit && ')'}
      </span>
    </div>
  );
}
